import { useEffect, useRef } from "react";

type Options = {
  ready: boolean;
  rowCount: number;
  projectName: string;
  title: string;
};

// Reports under ~200 rows are ready in seconds; the user is still on
// the tab and a notification would be more annoying than useful.
const MIN_ROWS_FOR_NOTIFICATION = 200;

/**
 * Fires a browser notification once the print view becomes ready, but
 * only if the tab is hidden at the moment of readiness. Permission is
 * requested lazily on the first tab-hide (popping the permission dialog
 * on mount is spammy). One-shot per mount.
 */
export function useReadyNotification({
  ready,
  rowCount,
  projectName,
  title
}: Options): void {
  const firedRef = useRef(false);
  const permissionAskedRef = useRef(false);

  useEffect(() => {
    if (rowCount < MIN_ROWS_FOR_NOTIFICATION) return undefined;
    if (typeof Notification === "undefined") return undefined;

    const onVisibilityChange = () => {
      if (permissionAskedRef.current) return;
      if (!document.hidden) return;
      if (Notification.permission !== "default") return;
      permissionAskedRef.current = true;
      Notification.requestPermission().catch(() => {
        /* ignore */
      });
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [rowCount]);

  useEffect(() => {
    if (firedRef.current) return;
    if (!ready) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (!document.hidden) return;

    firedRef.current = true;
    try {
      const n = new Notification("Print view ready", {
        body: `${title} for ${projectName} has finished rendering (${rowCount} rows).`,
        tag: "ciq-print-ready",
        icon: "/favicon.ico"
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      /* some browsers throw on constructor under permission races */
    }
  }, [ready, projectName, title, rowCount]);
}
